import { useMemoizedFn } from "ahooks";
import { useRef } from "react";

/**
 * @description: 创建一个只执行一次的方法
 * @param fn 要执行的方法
 * @returns 返回一个包装后的方法，该方法只会执行一次
 */
export function useOnce<T extends (...args: any[]) => any>(fn: T) {
  const hasExecutedRef = useRef(false);

  return useMemoizedFn((...args: Parameters<T>): ReturnType<T> | undefined => {
    if (hasExecutedRef.current) {
      return undefined;
    }

    hasExecutedRef.current = true;
    return fn(...args);
  });
}

/**
 * @description: 创建一个只执行一次的异步方法
 * @param fn 要执行的异步方法
 * @returns 返回一个包装后的异步方法，该方法只会执行一次
 */
export function useOnceAsync<T extends (...args: any[]) => Promise<any>>(
  fn: T
) {
  const hasExecutedRef = useRef(false);

  return useMemoizedFn(
    async (...args: Parameters<T>): Promise<ReturnType<T> | undefined> => {
      if (hasExecutedRef.current) {
        return undefined;
      }

      hasExecutedRef.current = true;
      // eslint-disable-next-line no-return-await
      return await fn(...args);
    }
  );
}

/**
 * @description: 返回一个只执行一次的方法，支持重置执行状态，允许方法再次执行
 * @param onceFn 由useOnce或useOnceAsync返回的方法
 * @returns 重置后的方法
 */
export function useResetOnce<T extends (...args: any[]) => any>(onceFn: T) {
  const hasExecutedRef = useRef(false);

  const resetFn = useMemoizedFn(
    (...args: Parameters<T>): ReturnType<T> | undefined => {
      if (hasExecutedRef.current) {
        return undefined;
      }

      hasExecutedRef.current = true;
      return onceFn(...args);
    }
  );

  const reset = useMemoizedFn(() => {
    hasExecutedRef.current = false;
  });

  return [resetFn, reset] as const;
}
